import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { LABEL_QR_ROLES, INVENTORY_SECTION_ROLES } from "../lib/roles";
import KunciDaruratFa from "../components/etams/KunciDaruratFa";
import AddendumFa from "../components/etams/AddendumFa";
import UpahBoronganFa from "../components/etams/UpahBoronganFa";
import StatusWaCustomer from "../components/etams/StatusWaCustomer";
import { useAuth } from "../contexts/AuthContext";
import { rupiah } from "../lib/statusLabels";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { FAStatusBadge, AdjustmentStatusBadge, PRStatusBadge } from "../components/etams/StatusBadge";
import { FAStatusBar } from "../components/etams/StatusBar";
import { useJudulHalaman } from "../components/etams/Breadcrumb";
import IsianTransferManual, { isianTransferAwal, isianTransferLengkap, muatanBukti } from "../components/etams/IsianTransferManual";
import { AlertCircle, CheckCircle2, XCircle, Clock } from "lucide-react";
import {
  useRealtimeTable,
  useVisiblePolling,
  REALTIME_DOWN_POLL_MS,
} from "../lib/useRealtimeTable";
import { formatDateTime, formatDate } from "../lib/format";
import { jamPesanan, useStoreTimezones } from "../lib/timezone";
import { ITEM_TYPE_TIGA, LABEL_ITEM_TYPE, WARNA_CELANA, itemTypeTersedia, modeHargaPaket, labelPaket, UKURAN_DEWASA } from "../lib/faKonfigurasi";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";
import { useKategoriHarga, labelKategori, labelDenganKode } from "../lib/kategoriHarga";
import { KartuLotFA } from "./inventory/TelusurLot";
import JalurTanpaDp from "../components/etams/JalurTanpaDp";
import { konfirmasi, mintaTeks } from "../lib/dialogAplikasi";
import { konfirmasiDenganMutasi } from "../lib/konfirmasiMutasi";

// Q68-01 Batch 3. Komponen lokal `StatusBadge` DIHAPUS (GAP-F: namanya bentrok
// dengan komponen kanonik). Tiga titik pakainya pindah ke tiga komponen yang
// BERBEDA, dan itu intinya: `badgeColor()` cuma menerima string telanjang,
// jadi ia tidak bisa membedakan `CANCELLED` milik FA dari `CANCELLED` milik
// adjustment. Sekarang tiap kelompok status punya petanya sendiri.
//
// Badge pembayaran memakai `PRStatusBadge` — `payment_records.status`, kolom
// yang MASIH HIDUP (ditulis `routers/payments.py` dengan tiga nilai
// MENUNGGU_KONFIRMASI/DIKONFIRMASI/DITOLAK). Ini BUKAN `fa_orders.payment_status`
// yang dihapus migration 004 dan dikunci `TEST-B1-01`; keduanya sempat
// berbunyi mirip, dan yang dirender layar ini selalu yang pertama.
//
// Ketiga badge di layar ini TANPA cincin, memakai default komponennya
// masing-masing. Cincin hilang dibanding tampilan lama — itu GAP-C opsi (a)
// yang dipilih Owner: seragam dengan CustomerDetail + 12 halaman purchasing,
// bukan mempertahankan cincin di dua halaman saja.

/** Bukti pembayaran yang menunggu vonis, dengan dua tombol untuk Owner/Kacab.
 *  CS melihat blok yang sama TANPA tombol — ia tahu buktinya sudah masuk dan
 *  sedang menunggu, bukan menebak dari badge. */
function KonfirmasiPembayaranInline({ pr, label, bolehPutus, bolehCek, isOwner, busy, callAction, testIdPrefix,
                                     jam = formatDateTime }) {
  const bukti = pr.proofs?.[0] || {};
  const [cek, setCek] = useState(null);
  if (pr.payment_method === "QRIS") {
    /* T30 (E1 Langkah 41): pembayaran DOKU tidak punya bukti transfer untuk dinilai — dulu tampil "Konfirmasi DP /
       Tolak" dengan bank & referensi kosong. Kini: keterangan otomatis + cek status sekarang. Konfirmasi manual hanya
       bila DOKU menyerahkannya ke Kepala Cabang (nominal beda / lingkungan menahan); tolak hanya Owner beralasan. */
    const keKc = cek && ["NOMINAL_BEDA_KE_KC", "DITAHAN_KC"].includes(cek.hasil);
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-doku`}>
        <div className="text-xs">
          <PRStatusBadge value={pr.status} /> <span className="text-muted-foreground">· {rupiah(pr.amount_idr)} · tautan DOKU dibuat {jam(pr.submitted_at)}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Dibayar lewat DOKU — terkonfirmasi otomatis begitu DOKU memberi kabar (biasanya beberapa menit, paling lambat sekitar 1 jam lewat pemeriksaan berkala).
        </p>
        <div className="flex flex-wrap gap-2">
          {bolehCek && (
            <Button size="sm" variant="outline" disabled={busy} data-testid={`${testIdPrefix}-cek-doku`}
                    onClick={() => callAction(async () => { const r = await api.post(`/payments/${pr.id}/cek-doku`); setCek(r.data); })}>
              Cek status ke DOKU sekarang
            </Button>
          )}
          {keKc && bolehPutus && (
            <Button size="sm" disabled={busy} data-testid={`${testIdPrefix}-confirm-btn`}
                    onClick={() => callAction(() => konfirmasiDenganMutasi(pr, label, { isOwner }))}>
              Konfirmasi {label}
            </Button>
          )}
          {isOwner && cek && cek.hasil !== "LUNAS_DIKONFIRMASI" && (
            <Button size="sm" variant="outline" disabled={busy} data-testid={`${testIdPrefix}-reject-btn`}
                    onClick={async () => { const r = await mintaTeks(`Alasan menolak ${label} DOKU ini (sesudah cek status)?`, { minPanjang: 10 }); if (r) callAction(() => api.post("/payments/reject", { payment_record_id: pr.id, reason: r })); }}>
              Tolak
            </Button>
          )}
        </div>
        {cek && <p className="text-xs" data-testid={`${testIdPrefix}-cek-hasil`}>{cek.pesan}</p>}
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-menunggu`}>
      <div className="text-xs">
        <PRStatusBadge value={pr.status} /> <span className="text-muted-foreground">· {rupiah(pr.amount_idr)} · diajukan {jam(pr.submitted_at)}</span>
      </div>
      {pr.bukti_kembar?.length > 0 && (
        /* T4: bukti transfer sama persis dengan bukti pembayaran lain — peringatan keras.
           K231: kembarannya SUDAH dikonfirmasi → DIBLOKIR (Kepala Cabang tanpa tombol konfirmasi; Owner buka beralasan). */
        <div className="rounded border border-danger-soft-border bg-danger-soft p-2 text-xs text-danger-soft-foreground font-medium"
             data-testid={`${testIdPrefix}-bukti-kembar`}>
          {pr.bukti_kembar_diblokir ? pr.kalimat_blokir_kembar : pr.peringatan_bukti_kembar}
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
        <div>Bank asal: <span className="text-foreground">{bukti.bank_from || "—"}</span></div>
        <div>Bank tujuan: <span className="text-foreground">{bukti.bank_to || "—"}</span></div>
        <div>No. referensi: <span className="text-foreground">{bukti.reference_number || "—"}</span></div>
        {bukti.notes && <div className="col-span-2">Catatan: <span className="text-foreground">{bukti.notes}</span></div>}
      </div>
      {bolehPutus ? (
        <div className="flex gap-2">
          {(!pr.bukti_kembar_diblokir || isOwner) && (
            <Button size="sm" disabled={busy} data-testid={`${testIdPrefix}-confirm-btn`}
                    variant={pr.bukti_kembar_diblokir ? "destructive" : "default"}
                    onClick={() => callAction(() => konfirmasiDenganMutasi(pr, label, { isOwner }))}>
              {pr.bukti_kembar_diblokir ? `Buka blokir & konfirmasi ${label}` : `Konfirmasi ${label}`}
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={busy} data-testid={`${testIdPrefix}-reject-btn`}
                  onClick={async () => { const r = await mintaTeks(`Alasan menolak ${label}?`); if (r) callAction(() => api.post("/payments/reject", { payment_record_id: pr.id, reason: r })); }}>
            Tolak
          </Button>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">Menunggu konfirmasi Owner / Kepala Cabang.</div>
      )}
    </div>
  );
}

/** T106 (titipan auditor 1 Nov 2026): DP/pelunasan yang SUDAH dikonfirmasi — nominal AKTUAL (K227), kapan, oleh siapa.
 *  Sumber = `GET /payments/tagihan/{id}` → `rincian_dibayar` (+ `dikonfirmasi_oleh` dari server). */
export function PembayaranTerkonfirmasi({ tagihan, jenis, label, jam = formatDateTime }) {
  const baris = (tagihan?.rincian_dibayar || []).filter((b) => b.jenis === jenis);
  if (!baris.length) return null;
  return (
    <div className="space-y-0.5 mb-1" data-testid={`fa-${jenis.toLowerCase()}-terkonfirmasi`}>
      {baris.map((b, i) => (
        <div key={b.payment_record_id || i} className="text-xs text-success-soft-foreground">
          {label} {rupiah(b.nominal_idr)} dikonfirmasi pada {jam(b.dikonfirmasi_pada)}
          {b.dikonfirmasi_oleh ? ` oleh ${b.dikonfirmasi_oleh}` : ""}.
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------
   Tambah item ke FA DRAFT yang SUDAH ADA — 11 September 2026 (vonis Owner (b)).

   ⛔ Sampai hari ini satu-satunya layar yang menulis item adalah FAWizard,
   dan wizard hanya jalan untuk DR yang BELUM punya FA (`DR_ALREADY_PROMOTED`).
   FA draft yang lahir tanpa item (penyemai, atau draft yang disimpan lalu
   ditinggal) nol punya layar untuk diberi item — celah nyata, bukan cuma
   halangan bukti. item_type DIPILIH dari `GET /fa-orders/{id}/pilihan-harga`
   (daftar harga wilayah FA), bukan diketik — PRASYARAT UAT Tahap 1.
   ------------------------------------------------------------------ */
const ITEM_SIZE_CODES = UKURAN_DEWASA;   // T21: tangga K106 (2XL, bukan XXL)
// U22b: kategori harga = data (`useKategoriHarga`); FA yang sudah punya kategori kepala
// (`fa_orders.kategori_harga_kode`, K188) mengunci pilihan ke kategori itu.

function TambahItemFa({ faId, fa, itemsAda, onSelesai }) {
  /* FA-TAMBAH-ITEM-PINTU-KEDUA-MASIH-BENTUK-LAMA-01 (22 Sep 2026): pintu KEDUA
     disamakan dengan wizard — tiga item_type K177 dari `pilihan-harga` FA
     (matriks `MATRIKS_SAH` lewat `itemTypeTersedia`: EVENT hanya Jersey saja),
     harga dari resolver yang sama (`POST /items`), warna celana kepala FA
     (K178 F4) diminta di sini bila baris set polos dan kepala belum punya. */
  const [pilihan, setPilihan] = useState(null);
  const [collars, setCollars] = useState([]);
  const kategoriHarga = useKategoriHarga();
  const kodeKepala = fa?.kategori_harga_kode || "";
  const [form, setForm] = useState({
    package_category: kodeKepala, item_type: "", qty_atasan: 12, size_code: "M",
    collar_id: itemsAda?.[0]?.collar_id || "", warna_celana: fa?.warna_celana || "", description: "",
  });
  const [sibuk, setSibuk] = useState(false);
  const [pesan, setPesan] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/fa-orders/${faId}/pilihan-harga`);
        const p = r.data?.pilihan || {};
        setPilihan(p);
        setForm((f) => isiBawaan(f, p));
      } catch { setPilihan({}); }
      try { const c = await api.get("/fa-orders/pilihan-collar"); setCollars(c.data?.items || []); } catch { setCollars([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faId]);

  useEffect(() => {
    if (!pilihan || kodeKepala || !kategoriHarga.length) return;
    setForm((f) => (f.package_category ? f : isiBawaan({ ...f, package_category: kategoriHarga[0].kode }, pilihan)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pilihan, kategoriHarga, kodeKepala]);

  function isiBawaan(f, p) {
    // kategori kepala FA mengunci (K188); FA lama tanpa kepala: kategori aktif pertama yang punya harga K177
    const paket = kodeKepala ? kodeKepala
      : modeHargaPaket(p, f.package_category) === "K177" ? f.package_category
      : (kategoriHarga.map((k) => k.kode).find((k) => modeHargaPaket(p, k) === "K177") || f.package_category);
    const tiga = itemTypeTersedia(p, paket);
    return { ...f, package_category: paket, item_type: tiga.includes(f.item_type) ? f.item_type : (tiga[0] || "") };
  }
  const ubah = (k) => (e) => setForm((f) => {
    const baru = { ...f, [k]: e.target.value };
    return k === "package_category" ? isiBawaan(baru, pilihan) : baru;
  });
  const tersedia = pilihan ? itemTypeTersedia(pilihan, form.package_category) : [];
  const butuhWarna = form.item_type === "SET_CELANA_POLOS" && !fa?.warna_celana;
  const alasan = !pilihan ? "Daftar harga wilayah belum termuat."
    : !tersedia.length ? `Kategori ${labelKategori(kategoriHarga, form.package_category)} belum punya harga di wilayah ini.`
    : !form.item_type ? "Pilih jenis item."
    : (butuhWarna && !form.warna_celana) ? "Baris Jersey set celana hitam/putih — pilih warna celana (sekali per FA)."
    : null;

  async function simpan() {
    setSibuk(true); setPesan(null);
    try {
      if (butuhWarna && form.warna_celana) {
        await api.patch(`/fa-orders/${faId}/konfigurasi`, { warna_celana: form.warna_celana });
      }
      await api.post(`/fa-orders/${faId}/items`, { items: [{
        item_type: form.item_type, qty_atasan: Number(form.qty_atasan),
        size_code: form.size_code || null, collar_id: form.collar_id || null,
        package_category: form.package_category, description: form.description || null,
      }] });
      setPesan("Item ditambahkan.");
      await onSelesai();
    } catch (e) {
      setPesan(apiErrorMessage(e, "Item gagal ditambahkan."));
    } finally { setSibuk(false); }
  }

  const sel = "w-full border border-input rounded px-2 py-1.5 text-sm";
  return (
    <div className="mt-4 border-t border-border pt-4" data-testid="fa-tambah-item">
      <h3 className="text-sm font-semibold mb-2">Tambah item</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label className="text-xs">Kategori harga</Label>
          <select className={sel} value={form.package_category} onChange={ubah("package_category")} disabled={!!kodeKepala} data-testid="fa-item-kategori"
                  title={kodeKepala ? "Kategori dikunci dari kepala FA (satu FA satu kategori)" : undefined}>
            {kodeKepala && <option value={kodeKepala}>{fa?.kategori_harga_label || labelKategori(kategoriHarga, kodeKepala)}</option>}
            {!kodeKepala && kategoriHarga.map((c) => <option key={c.kode} value={c.kode}>{labelDenganKode(kategoriHarga, c.kode)}</option>)}
          </select></div>
        <div><Label className="text-xs">Jenis</Label>
          <select className={sel} value={form.item_type} onChange={ubah("item_type")} disabled={!tersedia.length} data-testid="fa-item-type">
            {tersedia.length === 0 && <option value="">— belum ada harga —</option>}
            {ITEM_TYPE_TIGA.filter((t) => tersedia.includes(t)).map((t) => <option key={t} value={t}>{LABEL_ITEM_TYPE[t]}</option>)}
          </select></div>
        <div><Label className="text-xs">Jumlah (lembar)</Label><Input type="number" min={1} value={form.qty_atasan} onChange={ubah("qty_atasan")} data-testid="fa-item-qty" /></div>
        <div><Label className="text-xs">Ukuran</Label>
          <select className={sel} value={form.size_code} onChange={ubah("size_code")}>
            <option value="">— (tanpa tambahan ukuran)</option>{ITEM_SIZE_CODES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select></div>
        {butuhWarna && (
          <div><Label className="text-xs">Warna celana (kepala FA)</Label>
            <select className={sel} value={form.warna_celana} onChange={ubah("warna_celana")} data-testid="fa-item-warna-celana">
              <option value="">— pilih —</option>
              {WARNA_CELANA.map((w) => <option key={w} value={w}>{w === "HITAM" ? "Hitam" : "Putih"}</option>)}
            </select></div>
        )}
        <div><Label className="text-xs">Kerah</Label>
          <select className={sel} value={form.collar_id} onChange={ubah("collar_id")}>
            <option value="">— tanpa collar khusus —</option>
            {collars.map((c) => <option key={c.id} value={c.id}>{c.collar_name || c.collar_code}{c.surcharge_amount_idr ? ` (+${rupiah(c.surcharge_amount_idr)})` : ""}</option>)}
          </select></div>
        <div className="col-span-full"><Label className="text-xs">Deskripsi</Label><Input value={form.description} onChange={ubah("description")} /></div>
      </div>
      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">Cara harga dihitung <Hint>Harga dihitung sistem dari daftar harga wilayah, satu harga per lembar (set sudah termasuk celana). Jenis yang tersedia mengikuti Master Kategori Harga.</Hint></p>
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={simpan} disabled={sibuk || Boolean(alasan)} data-testid="fa-tambah-item-btn">{sibuk ? "Menyimpan…" : "Tambah item"}</Button>
        {alasan && <span className="text-sm text-warning-soft-foreground" data-testid="fa-tambah-item-terhalang">{alasan}</span>}
        {pesan && <span className={"text-sm " + (pesan === "Item ditambahkan." ? "text-success-soft-foreground" : "text-danger-soft-foreground")} data-testid="fa-tambah-item-pesan">{pesan}</span>}
      </div>
    </div>
  );
}

/** T22 (K222): pilihan customer di Ruang Pesanan, baca-saja, di kepala FA. */
function RingkasIsianCustomer({ isian }) {
  if (!isian) return null;
  const bagian = [
    [isian.warna, isian.kombinasi_warna].filter(Boolean).join(" · "),
    isian.kerah && `kerah ${isian.kerah}`, isian.lengan && `lengan ${String(isian.lengan).toLowerCase()}`,
    isian.pola && `pola ${String(isian.pola).toLowerCase()}`,
    (isian.sponsor || []).length ? `sponsor ${isian.sponsor.map((s) => [s.nama, s.posisi].filter(Boolean).join(" di ")).join(", ")}` : "",
    isian.jumlah_set ? `${isian.jumlah_set} jersey set` : "", isian.jumlah_atasan_saja ? `${isian.jumlah_atasan_saja} atasan saja` : "",
  ].filter(Boolean);
  if (!bagian.length) return null;
  return (
    <div className="text-xs text-muted-foreground mt-0.5" data-testid="fa-isian-customer">
      Pilihan customer: {bagian.join(" · ")}
    </div>
  );
}

/** T67: ubah jumlah/ukuran atau hapus satu baris FA draf (server menghitung ulang harga, beraudit). */
function BarisItemFa({ it, faId, bolehUbah, busy, callAction }) {
  const [ubah, setUbah] = useState(false);
  const [qty, setQty] = useState(it.qty_atasan);
  const [size, setSize] = useState(it.size_code || "");
  return (
    <tr className="border-b border-border" data-testid={`fa-item-row-${it.id}`}>
      <td className="py-2">{LABEL_ITEM_TYPE[it.item_type] || (it.product_type === "SET_ATASAN_CELANA" ? "Set atasan + celana (bentuk lama)" : "Atasan saja (bentuk lama)")}{it.description ? <span className="text-xs text-muted-foreground"> · {it.description}</span> : null}</td>
      <td>{ubah ? (
        <select className="border border-input rounded px-1 py-0.5 text-sm" value={size} onChange={(e) => setSize(e.target.value)} data-testid="fa-item-ubah-size">
          <option value="">—</option>{ITEM_SIZE_CODES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>) : (it.size_code || "—")}</td>
      <td className="text-right">{ubah ? <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="w-20 ml-auto" data-testid="fa-item-ubah-qty" /> : it.qty_atasan}</td>
      <td className="text-right">{rupiah(it.unit_price_atasan_idr)}</td>
      <td className="text-right font-medium">{rupiah(it.line_total_idr)}</td>
      {bolehUbah && (
        <td className="text-right whitespace-nowrap pl-2">
          {ubah ? (
            <>
              <Button size="sm" disabled={busy} data-testid="fa-item-simpan"
                      onClick={() => callAction(async () => { await api.patch(`/fa-orders/${faId}/items/${it.id}`, { qty_atasan: Number(qty), size_code: size || null }); setUbah(false); })}>Simpan</Button>
              <Button size="sm" variant="ghost" onClick={() => setUbah(false)}>Batal</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setUbah(true)} data-testid="fa-item-ubah">Ubah</Button>
              <Button size="sm" variant="ghost" disabled={busy} data-testid="fa-item-hapus"
                      onClick={async () => (await konfirmasi(`Hapus baris ${LABEL_ITEM_TYPE[it.item_type] || "ini"} ${it.size_code || ""} × ${it.qty_atasan}? Total FA dihitung ulang.`, { ya: "Hapus", bahaya: true }))
                        && callAction(() => api.delete(`/fa-orders/${faId}/items/${it.id}`))}>Hapus</Button>
            </>
          )}
        </td>
      )}
    </tr>
  );
}

// Status FA yang tidak lagi menagih pelunasan (cermin `services/bayar_ruang_pesanan.STATUS_TANPA_TAGIHAN`).
const STATUS_TANPA_TAGIHAN_PELUNASAN = ["LUNAS", "PELUNASAN_WAITING_CONFIRMATION", "FREE_OF_CHARGE", "CANCELLED",
  "SIAP_DISERAHKAN", "DISERAHKAN"];

const METODE_LABEL = {
  AMBIL_DI_CABANG: "Diambil sendiri di cabang",
  TITIP_AMBIL_CABANG: "Titip & ambil di cabang",
  KIRIM_KE_ALAMAT: "Kirim ke alamat",
};

function ringkasPengirimanFa(fa) {
  if (!fa?.metode_pengiriman) return "Belum dipilih customer";
  if (fa.metode_pengiriman === "TITIP_AMBIL_CABANG") {
    return `Titip & ambil di cabang ${fa.cabang_ambil?.name || "(cabang belum dipilih)"}`;
  }
  return METODE_LABEL[fa.metode_pengiriman] || fa.metode_pengiriman;
}

export default function FADetail() {
  useStoreTimezones();   // T93: jam menurut zona cabang FA + label WIB/WITA
  const { id } = useParams();
  const { profile } = useAuth();
  const role = profile?.role?.code;
  const isOwner = role === "OWNER";
  const isKacab = role === "KEPALA_CABANG";
  const isCS = role === "CS";

  const [data, setData] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState({ tanda_jadi: null, dp: null, pelunasan: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  // hasil `POST /isian-customer`: `perhatian` = apa yang MASIH harus dikerjakan
  // manusia (termasuk PENGIRIMAN_NOL_DISALIN — kenapa cara terima nol disalin)
  const [tarikHasil, setTarikHasil] = useState(null);
  // Remah roti memakai nomor FA sebagai butir terakhir, bukan uuid-nya.
  // Dipanggil di sini — SEBELUM early return — supaya urutan hook tetap.
  useJudulHalaman(data?.fa?.fa_number);
  const [busy, setBusy] = useState(false);
  const [roster, setRoster] = useState([]);

  // K234 / BUSINESS-113: isian transfer manual disederhanakan (rekening penerima dari master, tanggal otomatis,
  // bank pengirim & no. referensi opsional, wajib hanya bukti) — `IsianTransferManual`.
  const [dpProof, setDpProof] = useState(isianTransferAwal);
  // Inventaris C2: nominal DP = yang benar-benar ditransfer (bawaan = sisa DP wajib); KC mengonfirmasi, server menilai cukup/kurang.
  const [dpNominal, setDpNominal] = useState("");
  const [pelunasanProof, setPelunasanProof] = useState(isianTransferAwal);
  // Mandat 24 Sep A3 (Q91–Q93, K58 b3): tagihan induk + addendum, sudah dibayar, sisa/lebih.
  const [tagihan, setTagihan] = useState(null);
  // K208 (dulu K162): DP/pelunasan lewat DOKU — tombol hanya muncul bila backend menyatakan jalurnya BERLAKU
  // (syarat K208 + saklar mati cepat). Transfer manual tetap tersedia sebagai cadangan (dikonfirmasi KC, Q30).
  const [k162, setK162] = useState(false);
  // Drill F5 (Owner, hanya mode UAT + DOKU sandbox): tautan dengan notifikasi yang sengaja tidak sampai.
  const [ujiTertahanTersedia, setUjiTertahanTersedia] = useState(false);
  const [ujiTertahan, setUjiTertahan] = useState(false);
  const [tautanDoku, setTautanDoku] = useState(null);
  const peranSaya = profile?.role?.code;
  // K210 (BUSINESS-76): saklar DOKU per cabang — keadaan dinilai untuk region FA ini.
  const regionFa = data?.fa?.region_id;
  useEffect(() => {
    if (!["OWNER", "KEPALA_CABANG", "CS"].includes(peranSaya) || !regionFa) return;
    api.get("/payments/syarat-k162", { params: { region_id: regionFa } })
      .then((s) => { setK162(!!s.data?.berlaku); setUjiTertahanTersedia(!!s.data?.uji_notifikasi_tertahan_tersedia); })
      .catch(() => { setK162(false); setUjiTertahanTersedia(false); });
  }, [peranSaya, regionFa]);
  const [nominalPelunasan, setNominalPelunasan] = useState("");

  const load = useCallback(async () => {
    // ⛔ `setLoading(true)` DIHAPUS dari sini, 2 Sep 2026. Polling 4 detik
    //    memanggil load() berulang; tiap panggilan mengganti SELURUH layar
    //    dengan "Memuat…" selama lima await berantai — beberapa detik tiap
    //    empat detik. Ditemukan saat menelusuri: tombol yang mau diklik
    //    hilang di bawah tangan. Muatan PERTAMA tetap "Memuat…" lewat nilai
    //    awal useState(true); penyegaran berikutnya berjalan diam.
    setErr(null);
    try {
      const r = await api.get(`/fa-orders/${id}`);
      setData(r.data);
      const fa = r.data.fa;
      // Customer
      if (fa.customer_id) {
        try { const c = await api.get(`/customers/${fa.customer_id}`); setCustomer(c.data); } catch { /* */ }
      }
      // Roster
      try { const rs = await api.get(`/fa-orders/${id}/roster`); setRoster(rs.data?.items || []); } catch { setRoster([]); }
      // Tanda Jadi payment (from DR)
      let tj = null;
      if (fa.design_request_id) {
        try {
          const dr = await api.get(`/design-requests/${fa.design_request_id}`);
          if (dr.data?.tanda_jadi_payment_record_id) {
            const pr = await api.get(`/payments/${dr.data.tanda_jadi_payment_record_id}`);
            tj = pr.data;
          }
        } catch { /* */ }
      }
      // DP + Pelunasan: scan via fa-orders detail not provided → find via payment_records list per FA.
      // No backend endpoint to list payments by fa_id, so we read what we know: scan recent payment records by fa_id is not exposed.
      // Workaround: rely on per-payment fetch when known; for fresh FA, just track from this UI only.
      // To recover after refresh, we expose a quick scan by searching design_requests/payments not available.
      // Pragmatic: list pending payments via /api/payments has no endpoint either. Document.
      // DP + Pelunasan: auto-discover via additive C3a-2β-i endpoint.
      let dp = null, pel = null;
      try {
        const lst = await api.get("/payments", { params: { fa_id: id } });
        for (const pr of (lst.data?.items || [])) {
          if (pr.payment_type === "DP" && !dp) dp = pr;
          else if (pr.payment_type === "PELUNASAN" && !pel) pel = pr;
        }
      } catch { /* */ }
      setPayments({ tanda_jadi: tj, dp, pelunasan: pel });
      try { const tg = await api.get(`/payments/tagihan/${id}`); setTagihan(tg.data); } catch { setTagihan(null); }
    } catch (e) {
      setErr(apiErrorMessage(e, "FA gagal dimuat."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Jeda polling layar detail. 8 detik MENIRU DesignRequestDetail dan
  // WODetail, dua layar detail lain di repo ini yang sama-sama memakai 8000 —
  // layar yang sedang dikerjakan, bukan sekadar dilihat sekilas.
  const FA_DETAIL_POLL_MS = 8000;

  // Realtime jalur utama; polling jaring pengaman (D108). Sebelum ini FADetail
  // NOL polling — ia menyegar setelah AKSI SENDIRI lewat callAction(), jadi
  // perubahan yang dilakukan orang lain tidak pernah terlihat sampai halaman
  // dibuka ulang.
  const rtFa = useRealtimeTable("fa_orders", load);
  const rtPay = useRealtimeTable("payment_records", load);
  // Cukup SATU channel putus untuk mempercepat polling: masing-masing membawa
  // perubahan yang tidak dibawa yang lain, jadi menunggu keduanya putus berarti
  // membiarkan separuh layar basi tanpa jaring.
  useVisiblePolling(
    load,
    rtFa && rtPay ? FA_DETAIL_POLL_MS : REALTIME_DOWN_POLL_MS
  );

  useEffect(() => { load(); }, [load]);

  // S4 order dobel (K109, vonis §14): kembaran terdeteksi → CS menulis alasan lalu FA tetap terkirim.
  // T19: FA yang jumlahnya beda dari daftar nama customer tidak terkirim tanpa alasan tertulis.
  async function kirimFa(muatan = {}) {
    try {
      return await api.post(`/fa-orders/${id}/submit`, muatan);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status !== 409) throw e;
      if (d?.error === "JUMLAH_BEDA_DAFTAR_NAMA") {
        const alasan = await mintaTeks(`${d.message}

Alasan mengirim dengan jumlah berbeda:`, { judul: "Jumlah beda dari daftar nama", minPanjang: d.min_alasan || 10 });
        if (!alasan) return null;
        return kirimFa({ ...muatan, alasan_jumlah: alasan });
      }
      if (d?.error !== "FA_DOBEL_TERDETEKSI") throw e;
      const daftar = (d.kembaran || []).map((k) => k.fa_number || k.id).join(", ");
      const alasan = await mintaTeks(`${d.message}

FA kembar: ${daftar}

Alasan melanjutkan:`, { judul: "Order dobel terdeteksi", minPanjang: d.min_alasan || 10 });
      if (!alasan) return null;
      return kirimFa({ ...muatan, alasan_dobel: alasan });
    }
  }

  // T26 / K124 butir 4: persetujuan FA milik customer. Tombol staf = cadangan — tanpa konfirmasi customer, alasan +
  // bukti wajib (server yang menegakkan).
  async function catatPersetujuan(fa) {
    if (fa.approval_client_signed_at) return api.post(`/fa-orders/${id}/approve`, {});
    const alasan = await mintaTeks("Customer belum menekan \"Saya konfirmasi benar\" di Ruang Pesanan. Bila customer sudah menyetujui lewat jalur lain, tulis alasannya:",
      { judul: "Catat persetujuan customer", minPanjang: 10 });
    if (!alasan) return null;
    const bukti = await mintaTeks("Bukti persetujuan customer (mis. tangkapan layar chat WA pukul berapa, atau catatan telepon):",
      { judul: "Bukti persetujuan", minPanjang: 5 });
    if (!bukti) return null;
    return api.post(`/fa-orders/${id}/approve`, { alasan, bukti });
  }

  async function callAction(fn) {
    setBusy(true);
    setActionErr(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setActionErr(apiErrorMessage(e, "Aksi gagal. Coba lagi."));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="p-6 text-sm text-muted-foreground" data-testid="fa-detail-loading">Memuat...</div>;
  if (err || !data) return <div className="p-6 text-danger-soft-foreground bg-danger-soft border border-danger-soft-border rounded text-sm" data-testid="fa-detail-error">{err || "Tidak ditemukan."}</div>;

  const fa = data.fa;
  // T93 (contoh Owner: "DP dikonfirmasi pada … 11.10" untuk konfirmasi 10.10 WIB): jam pembayaran = zona cabang FA + label.
  const jamFa = (d) => jamPesanan(d, fa.store_id, fa.region_id);
  const items = data.items || [];
  const adjs = data.adjustments || [];
  const breakdown = data.pricing_breakdown || {};
  const deadline = data.deadline_date;

  // --- P4: dokumen customer (tagihan + surat jalan) ----------------------
  // ⚠ Tab dibuka DULU, baru diisi — window.open sesudah await diblokir
  //   peramban sebagai popup (pola PdfArchive.jsx, alasannya di sana).
  async function bukaDokumen(kind) {
    setBusy(true); setActionErr(null);
    const tab = window.open("", "_blank");
    try {
      const g = await api.post("/pdf/generate", { kind, related_entity_id: id });
      const su = await api.get(`/pdf/documents/${g.data.id}/signed-url`);
      const url = su.data.signed_url || su.data.url;
      if (!url) throw new Error("Tautan berkas kosong");
      if (tab) tab.location.href = url; else window.location.href = url;
    } catch (e) {
      if (tab) tab.close();
      setActionErr(apiErrorMessage(e, "Dokumen gagal dibuka. Coba lagi."));
    } finally { setBusy(false); }
  }
  const bolehDokumen = isCS || isKacab || isOwner;
  // Tagihan: apa pun kecuali draft. Surat jalan: tombolnya tampil saat uang beres (LUNAS/FOC) atau sesudahnya
  // (siap/diserahkan) — backend tetap pagar utamanya, termasuk "barang belum jadi" untuk lunas dini.
  const canTagihan = bolehDokumen && fa.order_status !== "DRAFT_FA_CREATED";
  const canSuratJalan = bolehDokumen
    && ["LUNAS", "FREE_OF_CHARGE", "SIAP_DISERAHKAN", "DISERAHKAN"].includes(fa.order_status);
  // Label QR (S11 "cukup scan, tidak ketik") — sensus pintu 23 Sep 2026: rutenya berdiri tanpa
  // tombol. Scan membuka /serah-terima?fa=<id>. Backend (`label_qr`) tetap penjaga peran+wilayah.
  const canLabelQr = LABEL_QR_ROLES.includes(role) && fa.order_status !== "DRAFT_FA_CREATED";
  async function bukaLabelQr() {
    setBusy(true); setActionErr(null);
    const tab = window.open("", "_blank");   // dibuka DULU — sesudah await diblokir peramban
    try {
      const r = await api.get(`/label/fa/${id}/qr.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (tab) tab.location.href = url; else window.location.href = url;
    } catch (e) {
      if (tab) tab.close();
      setActionErr(apiErrorMessage(e, "Label QR gagal dibuka. Coba lagi."));
    } finally { setBusy(false); }
  }

  const canSubmit = isCS && fa.order_status === "DRAFT_FA_CREATED";
  // Tambah item: pagar backend add_items = _fa_scope_check(write) + DRAFT_FA_CREATED
  const canTambahItem = (isOwner || isKacab || isCS) && fa.order_status === "DRAFT_FA_CREATED";
  // Sambungan KELIMA (11 Sep 2026): tarik isian customer dari Ruang Pesanan.
  // Backend: OWNER / KEPALA_CABANG (region) / CS (store); status DRAFT|WAITING;
  // brief wajib terkunci; ditolak sesudah customer konfirmasi (S2).
  const canTarikIsian = (isOwner || isKacab || isCS) && Boolean(fa.design_request_id)
    && ["DRAFT_FA_CREATED", "FA_WAITING_CUSTOMER_APPROVAL"].includes(fa.order_status)
    && !fa.approval_client_signed_at;
  const canApprove = (isOwner || isKacab) && fa.order_status === "FA_WAITING_CUSTOMER_APPROVAL";
  const canReject = (isOwner || isKacab) && ["DRAFT_FA_CREATED", "FA_WAITING_CUSTOMER_APPROVAL"].includes(fa.order_status);

  // BUSINESS-81: FA FOC tidak ditagih DP (server menolak FA_FOC_TANPA_DP); addendum-nya dibayar sebagai pelunasan.
  const canSubmitDP = isCS && fa.order_status === "FA_APPROVED_WAITING_DP" && !fa.is_foc;
  // `fa.pelunasan_confirmed_at` NOL ADA di skema (diukur 2 Sep 2026) — selalu
  // undefined, jadi syarat lama selalu lolos. Kebenaran pelunasan = LUNAS.
  const canSubmitPelunasan = isCS && (fa.dp_confirmed_at || fa.is_foc)
    && !["LUNAS", "PELUNASAN_WAITING_CONFIRMATION", "FREE_OF_CHARGE", "CANCELLED"].includes(fa.order_status)
    && !(payments.pelunasan?.status === "MENUNGGU_KONFIRMASI");

  return (
    <div className="max-w-6xl space-y-6" data-testid="fa-detail-root">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="fa-number">{fa.fa_number}</h1>
          {/* Nama team ditaruh SEBARIS DI BAWAH nomor FA, bukan digabung ke
              baris customer: ia yang paling sering dipakai staf untuk mengenali
              pesanan ("yang basket SMA 5"), dan menggabungkannya ke deretan
              titik-pemisah membuatnya hilang di antara paket dan hari kerja. */}
          {fa.nama_team && (
            <div className="text-sm font-medium text-foreground mt-0.5" data-testid="fa-nama-team">
              {fa.nama_team}
            </div>
          )}
          <div className="text-sm text-muted-foreground mt-1">
            {customer?.name || "—"}
            {customer?.city ? ` (${customer.city})` : ""} · {labelPaket(fa.package_type)} ({fa.package_days} hari kerja)
            {data.kalimat_estimasi && <div className="text-xs text-muted-foreground" data-testid="fa-kalimat-estimasi">{data.kalimat_estimasi}</div>}
            {fa.disisipkan_pada && <span className="ml-2 text-xs font-medium text-success-soft-foreground" data-testid="fa-sisipan-badge">· disisipkan: diproduksi Express tanpa biaya</span>}
            {fa.kategori_harga_label && <span data-testid="fa-kategori-harga"> · Kategori {fa.kategori_harga_label}</span>}
          </div>
          {/* T22 (K222): pola, kerah, warna celana di kepala FA + pilihan customer dari Ruang Pesanan. */}
          <div className="text-xs text-muted-foreground mt-1" data-testid="fa-kepala-konfigurasi">
            Pola {fa.pola_baju === "RAGLAN" ? "Raglan" : fa.pola_baju === "REGULER" ? "Reguler" : "—"}
            {" · "}Kerah {data.kerah_nama || "—"}
            {fa.warna_celana ? ` · Celana polos ${fa.warna_celana === "HITAM" ? "hitam" : "putih"}` : ""}
          </div>
          <RingkasIsianCustomer isian={data.isian_customer} />
        </div>
        <div className="flex flex-col items-end gap-2">
          <FAStatusBadge value={fa.order_status} />
          {/* T69 + T26: tombol utama FA di dekat status; konfirmasi customer ditampilkan di sini. */}
          {fa.approval_client_signed_at && (
            <div className="text-xs text-success-soft-foreground" data-testid="fa-customer-konfirmasi">
              Customer sudah konfirmasi {jamPesanan(fa.approval_client_signed_at, fa.store_id, fa.region_id)}
            </div>
          )}
          {fa.order_status === "FA_WAITING_CUSTOMER_APPROVAL" && !fa.approval_client_signed_at && (
            <div className="text-xs text-muted-foreground" data-testid="fa-menunggu-customer">Menunggu customer mengonfirmasi di Ruang Pesanan</div>
          )}
          {(canSubmit || canApprove || canReject) && (
            <div className="flex flex-wrap justify-end gap-2" data-testid="fa-aksi-utama">
              {canSubmit && (
                <Button size="sm" onClick={() => callAction(() => kirimFa())} disabled={busy} data-testid="fa-submit-btn">Kirim FA ke customer</Button>
              )}
              {canApprove && (
                <Button size="sm" variant="outline" onClick={() => callAction(() => catatPersetujuan(fa))} disabled={busy} data-testid="fa-approve-btn">
                  <CheckCircle2 size={14} className="mr-1" />Catat persetujuan customer
                </Button>
              )}
              {canReject && (
                <Button size="sm" variant="outline" onClick={async () => { const r = await mintaTeks("Alasan menolak FA ini?"); if (r) callAction(() => api.post(`/fa-orders/${id}/reject`, { reason: r })); }} disabled={busy} data-testid="fa-reject-btn">
                  <XCircle size={14} className="mr-1" />Tolak FA
                </Button>
              )}
            </div>
          )}
          <div className="flex gap-2">
            {canTagihan && (
              <Button size="sm" variant="outline" onClick={() => bukaDokumen("FA")}
                      disabled={busy} data-testid="fa-btn-tagihan">
                Tagihan PDF
              </Button>
            )}
            {canSuratJalan && (
              <Button size="sm" variant="outline" onClick={() => bukaDokumen("surat_jalan")}
                      disabled={busy} data-testid="fa-btn-surat-jalan">
                Surat Jalan PDF
              </Button>
            )}
            {canLabelQr && (
              <Button size="sm" variant="outline" onClick={bukaLabelQr}
                      disabled={busy} data-testid="fa-btn-label-qr">
                Label QR
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* T29: WO yang memuat FA ini — staf tidak perlu mencari di daftar WO. */}
      {(data?.wo || []).length > 0 && (
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm" data-testid="fa-masuk-wo">
          Masuk WO:{" "}
          {data.wo.map((w, i) => (
            <span key={w.id}>
              {i > 0 && ", "}
              <Link to={`/wo/${w.id}`} className="font-mono underline">{w.wo_number}</Link>
              {w.status === "DRAFT" ? " (masih draf)" : ""}
            </span>
          ))}
        </div>
      )}
      {/* K52 butir 4 — unlock darurat FA, pintu Kepala Cabang (AUDIT-B2 23 Sep 2026). */}
      {isKacab && <KunciDaruratFa fa={{ ...fa, id }} onSelesai={load} />}
      {/* K58 — addendum: hubungan induk↔anak + tombol buat (AUDIT-B2 23 Sep 2026). Tombol untuk
          pembuat FA; syarat terkunci/belum-WO ditegakkan trigger, bukan ditebak layar. */}
      <AddendumFa fa={{ ...fa, id }} addenda={data?.addenda} induk={data?.induk}
                  bolehBuat={(isOwner || isKacab || isCS) && !fa.parent_fa_id && fa.order_status !== "DRAFT_FA_CREATED" && fa.order_status !== "CANCELLED"} />
      {/* VONIS-24SEP-BORONGAN: rencana upah borongan (tarif beku saat FA dibuat), group jahit, koreksi tarif. */}
      <UpahBoronganFa faId={id} />
      {/* T45: status tiap WhatsApp ke customer FA ini (Owner · KC · CS) — "Terkirim" belum berarti sampai. */}
      <StatusWaCustomer sumber="fa" id={id} />

      {/* Status bar tahapan (Tahap 5 ERP) — di bawah kepala, sebaris penuh:
          sembilan langkah nol muat di kolom kanan bersama tombol PDF. Badge
          di atas TETAP: ia yang bicara untuk keadaan samping (FOC, batal,
          exception), yang nol punya tempat di garis. */}
      <FAStatusBar value={fa.order_status} />

      {actionErr && (
        <div className="flex items-start gap-2 p-3 rounded border border-danger-soft-border bg-danger-soft text-danger-soft-foreground text-sm" data-testid="fa-action-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span className="break-all">{actionErr}</span>
        </div>
      )}

      {/* Items & Pricing */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Item & Harga</h2>
          <div className="text-xs text-muted-foreground">{items.length} item</div>
        </div>
        <table className="w-full text-sm" data-testid="fa-items-table">
          <thead className="text-xs text-muted-foreground border-b border-border">
            <tr><th className="text-left py-2">Jenis</th><th className="text-left">Ukuran</th><th className="text-right">Qty</th><th className="text-right">Harga satuan</th><th className="text-right">Jumlah</th>{canTambahItem && <th></th>}</tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <BarisItemFa key={it.id} it={it} faId={id} bolehUbah={canTambahItem} busy={busy} callAction={callAction} />
            ))}
          </tbody>
        </table>
        <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-1 text-sm" data-testid="fa-pricing-breakdown">
          <div className="text-muted-foreground">Subtotal dasar</div><div className="text-right">{rupiah(breakdown.subtotal_base_idr)}</div>
          <div className="text-muted-foreground">Tambahan ukuran</div><div className="text-right">{rupiah(breakdown.surcharges_breakdown?.size_idr)}</div>
          <div className="text-muted-foreground">Tambahan kerah</div><div className="text-right">{rupiah(breakdown.surcharges_breakdown?.collar_idr)}</div>
          <div className="text-muted-foreground">Tambahan paket waktu</div><div className="text-right">{rupiah(breakdown.surcharges_breakdown?.time_package_idr)}</div>
          <div className="text-muted-foreground">Diskon disetujui</div><div className="text-right">-{rupiah(breakdown.adjustments_breakdown?.approved_discount_idr)}</div>
          <div className="font-semibold border-t pt-1 mt-1">Total Akhir</div><div className="text-right font-bold border-t pt-1 mt-1" data-testid="fa-grand-total">{rupiah(breakdown.grand_total_idr)}</div>
          <div className="text-muted-foreground">DP ({fa.dp_percentage}%)</div><div className="text-right">{rupiah(breakdown.dp_amount_idr)}</div>
          {/* T88 / K227: pelunasan dari tanda jadi yang BENAR-BENAR diterima (`tagihan.rencana`), bukan kolom pengajuan. */}
          <div className="text-muted-foreground">Pelunasan{tagihan?.rencana ? ` (sesudah tanda jadi ${rupiah(tagihan.rencana.tanda_jadi_idr)} dan DP)` : ""}</div>
          <div className="text-right" data-testid="fa-rincian-pelunasan">{rupiah(tagihan?.rencana ? tagihan.rencana.pelunasan_idr : breakdown.pelunasan_amount_idr)}</div>
        </div>
        {canTambahItem && <TambahItemFa faId={id} fa={fa} itemsAda={items} onSelesai={load} />}
      </Card>

      {/* Adjustments */}
      {adjs.length > 0 && (
        <Card className="p-6">
          <h2 className="font-semibold mb-3">Penyesuaian Khusus</h2>
          <table className="w-full text-sm" data-testid="fa-adjustments-table">
            <thead className="text-xs text-muted-foreground border-b">
              <tr><th className="text-left py-2">Jenis</th><th className="text-left">Alasan</th><th className="text-right">Diskon</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {adjs.map((a) => {
                const canActOnThis = (isOwner || (isKacab && a.approver_required_role === "KEPALA_CABANG"))
                  && a.approval_status === "PENDING_APPROVAL"
                  && a.created_by_cs_id !== profile?.id;
                return (
                  <tr key={a.id} className="border-b border-border" data-testid={`fa-adj-row-${a.id}`}>
                    <td className="py-2">{(a.adjustment_type || "—").replace(/_/g, " ").toLowerCase()}</td>
                    <td className="text-xs">{a.reason}</td>
                    <td className="text-right">{rupiah(a.discount_amount_idr)}</td>
                    <td><AdjustmentStatusBadge value={a.status} /></td>
                    <td className="text-right">
                      {canActOnThis && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => callAction(() => api.post(`/fa-orders/${id}/adjustments/${a.id}/approve`, { note: "ok" }))} disabled={busy} data-testid={`fa-adj-approve-${a.id}`}>Setujui</Button>
                          <Button size="sm" variant="outline" onClick={async () => { const r = await mintaTeks("Alasan menolak penyesuaian ini?"); if (r) callAction(() => api.post(`/fa-orders/${id}/adjustments/${a.id}/reject`, { note: r })); }} disabled={busy} data-testid={`fa-adj-reject-${a.id}`}>Tolak</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Pengiriman — baca-saja. Sumbernya fa_orders (jawaban TERAKHIR
          customer, bentuk A); yang menulisnya: tarik isian (isi pertama),
          customer di tahap FA Ruang Pesanan, atau CS/KC lewat ubah cara terima. */}
      <Card className="p-6" data-testid="fa-pengiriman">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-semibold mb-1">Pengiriman</h2>
            <p className="text-sm" data-testid="fa-pengiriman-ringkas">{ringkasPengirimanFa(fa)}</p>
            {fa.metode_pengiriman === "KIRIM_KE_ALAMAT" && fa.alamat_kirim && (
              <p className="text-sm text-muted-foreground whitespace-pre-line mt-1">{fa.alamat_kirim}</p>
            )}
          </div>
          {canTarikIsian && (
            <Button
              variant="outline"
              disabled={busy}
              data-testid="fa-tarik-isian-btn"
              onClick={() => callAction(async () => {
                const r = await api.post(`/fa-orders/${id}/isian-customer`, {});
                setTarikHasil(r.data);
              })}
            >
              Tarik isian customer
            </Button>
          )}
        </div>
        {tarikHasil && (
          <div className="mt-3 text-sm space-y-1" data-testid="fa-tarik-hasil">
            <p>Roster dipindahkan: {tarikHasil.baris_roster} baris.</p>
            {(tarikHasil.perhatian || []).map((p) => (
              /* T25a: "ambil sendiri" / "titip ambil" adalah kabar, bukan peringatan. */
              <p key={p.kode} className={["AMBIL_SENDIRI", "TITIP_AMBIL"].includes(p.kode) ? "text-muted-foreground" : "text-warning-soft-foreground"} data-testid={`fa-perhatian-${p.kode}`}>
                {p.pesan}
              </p>
            ))}
          </div>
        )}
      </Card>

      {/* Roster — T23: ukuran anak, jenis (set / atasan saja), ukuran celana, lengan; gender hanya bila terisi. */}
      {roster.length > 0 && (() => {
        const perItem = Object.fromEntries(items.map((it) => [it.id, it]));
        const adaGender = roster.some((r) => r.gender);
        const adaCelana = roster.some((r) => r.ukuran_celana);
        return (
          <Card className="p-6">
            <h2 className="font-semibold mb-3">Daftar nama ({roster.length})</h2>
            <table className="w-full text-sm" data-testid="fa-roster-table">
              <thead className="text-xs text-muted-foreground border-b"><tr>
                <th className="text-left py-2">#</th><th className="text-left">Nama</th><th className="text-left">No</th>
                <th className="text-left">Jenis</th><th className="text-left">Ukuran</th>
                {adaCelana && <th className="text-left">Ukuran celana</th>}<th className="text-left">Lengan</th>
                {adaGender && <th className="text-left">Gender</th>}
              </tr></thead>
              <tbody>{roster.map((r) => {
                const it = perItem[r.fa_item_id];
                return (
                  <tr key={r.id} className="border-b border-border">
                    <td className="py-2">{r.row_no}</td><td>{r.nameset_punggung}</td><td>{r.nomor_punggung}</td>
                    <td>{it ? (LABEL_ITEM_TYPE[it.item_type] || "—") : "—"}</td>
                    <td>{r.ukuran_dewasa || (r.ukuran_anak ? `Anak ${r.ukuran_anak}` : "—")}</td>
                    {adaCelana && <td>{r.ukuran_celana || "—"}</td>}
                    <td>{r.jenis_lengan === "PANJANG" ? "Panjang" : r.jenis_lengan === "PENDEK" ? "Pendek" : "—"}</td>
                    {adaGender && <td>{r.gender || "—"}</td>}
                  </tr>
                );
              })}</tbody>
            </table>
          </Card>
        );
      })()}

      {/* FA action buttons */}
      <div className="flex flex-wrap gap-2">
        {profile?.role?.code === "ADMIN_PRODUKSI" && fa.package_type === "REGULER" && !fa.disisipkan_pada && (
          <Button variant="outline" disabled={busy} data-testid="fa-sisipan-cepat-btn"
                  onClick={async () => (await konfirmasi("Sisipkan repeat order ini (≤ 50 lembar) ke ritme EXPRESS tanpa biaya tambahan?")) && callAction(() => api.post(`/fa-orders/${id}/sisipan-cepat`))}>
            Sisipan cepat (repeat)
          </Button>
        )}
        {/* T69: Kirim FA / Catat persetujuan / Tolak FA pindah ke kepala halaman, dekat status. */}
      </div>

      {/* S6 sisa (ISIAN §17): lot kain yang dipakai FA ini — peran baca inventori. */}
      {INVENTORY_SECTION_ROLES.includes(profile?.role?.code) && <KartuLotFA faId={id} />}

      {/* Payment Timeline */}
      <Card className="p-6 space-y-6" data-testid="fa-payment-timeline">
        <h2 className="font-semibold">Alur Pembayaran</h2>
        {/* K197 butir 4: jalur tanpa DP — FOC dari persetujuan FOC · Special disetujui KC per FA. */}
        {fa && <JalurTanpaDp fa={fa} peran={peranSaya} profileId={profile?.id} onBerubah={load} />}
        {k162 && (
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="fa-doku-k162">
            {/* K210 BUSINESS-77: tautan pelunasan tersedia SEJAK DP dikonfirmasi (pelunasan dini; nominal = sisa
                tagihan, dihitung server); tautan uang muka hanya sebelum DP dikonfirmasi. */}
            {/* BUSINESS-81: FA FOC tidak punya DP; addendum-nya ditagih sebagai pelunasan (nominal Rp0 = ditolak server). */}
            {/* T25b (E1 Langkah 38): tautan uang muka hanya SESUDAH FA disetujui customer — dulu tampil sejak draf
                dan ditolak server tanpa penjelasan. */}
            {["DP", "PELUNASAN"].filter((j) => (j === "DP"
              ? !fa.dp_confirmed_at && !fa.is_foc && ["FA_APPROVED_WAITING_DP", "DP_WAITING_CONFIRMATION"].includes(fa.order_status)
              : (!!fa.dp_confirmed_at || !!fa.is_foc) && !STATUS_TANPA_TAGIHAN_PELUNASAN.includes(fa.order_status))).map((j) => (
              <Button key={j} size="sm" variant="outline" disabled={busy} data-testid={`fa-doku-${j}`}
                      onClick={() => callAction(async () => {
                        const r = await api.post("/payments/fa/doku", {
                          fa_id: id, payment_type: j,
                          ...(ujiTertahan && peranSaya === "OWNER" ? { uji_notifikasi_tertahan: true } : {}),
                        });
                        setTautanDoku(r.data);
                        setUjiTertahan(false);   // T25c: centang uji tidak terbawa ke tautan berikutnya
                      })}>
                Tautan DOKU {j === "DP" ? "uang muka" : "pelunasan"}
              </Button>
            ))}
            {!fa.dp_confirmed_at && !fa.is_foc && ["DRAFT_FA_CREATED", "FA_WAITING_CUSTOMER_APPROVAL"].includes(fa.order_status) && (
              <span className="text-xs text-muted-foreground" data-testid="fa-doku-belum-waktunya">Tautan uang muka tersedia sesudah customer mengonfirmasi FA.</span>
            )}
            {ujiTertahanTersedia && peranSaya === "OWNER" && (
              /* T25c: kotak uji dipisah & berwarna peringatan supaya tidak tercentang tak sengaja. */
              <label className="basis-full flex items-center gap-1 text-xs rounded border border-warning-soft-border bg-warning-soft text-warning-soft-foreground px-2 py-1" data-testid="fa-doku-uji-tertahan">
                <input type="checkbox" checked={ujiTertahan} onChange={(e) => setUjiTertahan(e.target.checked)} />
                Hanya untuk uji di UAT — jangan dicentang untuk pesanan sungguhan: kabar pembayaran sengaja ditahan dan baru tercatat lewat pemeriksaan berkala.
              </label>
            )}
            {tautanDoku && <a href={tautanDoku.url} target="_blank" rel="noreferrer" className="underline">{tautanDoku.invoice_number} · {rupiah(tautanDoku.amount_idr)}</a>}
          </div>
        )}
        {tagihan && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-md border border-border p-3 text-sm" data-testid="fa-tagihan-ringkas">
            <div><div className="text-xs text-muted-foreground">Total tagihan{tagihan.foc ? (tagihan.addendum?.length ? " (FOC: hanya addendum)" : " (FOC: tanpa biaya)") : tagihan.addendum?.length ? " (induk + addendum)" : ""}</div><div className="font-medium tabular-nums">{rupiah(tagihan.total_tagihan_idr)}</div>
              {/* BUSINESS-81 + BUSINESS-86 (K214): tanda jadi FA FOC memotong tagihan tambahan pesanan; sisanya tidak
                  dikembalikan. */}
              {tagihan.foc && tagihan.tanda_jadi_foc_idr > 0 && (
                <div className="text-xs text-muted-foreground" data-testid="fa-tagihan-tj-foc">
                  {tagihan.tanda_jadi_foc_dipotong_idr > 0
                    ? `Tanda jadi ${rupiah(tagihan.tanda_jadi_foc_idr)}: ${rupiah(tagihan.tanda_jadi_foc_dipotong_idr)} dipotongkan ke tambahan pesanan`
                      + (tagihan.tanda_jadi_foc_tidak_kembali_idr > 0 ? `, ${rupiah(tagihan.tanda_jadi_foc_tidak_kembali_idr)} tidak dikembalikan` : "")
                    : `Tanda jadi ${rupiah(tagihan.tanda_jadi_foc_idr)} tidak dikembalikan`}
                </div>
              )}
              {tagihan.potongan_refund_idr > 0 && (
                <div className="text-xs text-muted-foreground" data-testid="fa-tagihan-potongan">
                  Dipotong pengembalian dana {rupiah(tagihan.potongan_refund_idr)}
                  {(tagihan.rincian_potongan || []).length ? ` (${tagihan.rincian_potongan.map((b) => b.refund_number).filter(Boolean).join(", ")})` : ""}
                </div>
              )}
            </div>
            <div><div className="text-xs text-muted-foreground">Sudah dibayar (dikonfirmasi)</div><div className="font-medium tabular-nums">{rupiah(tagihan.sudah_dibayar_idr)}</div></div>
            <div><div className="text-xs text-muted-foreground">Menunggu konfirmasi</div><div className="tabular-nums">{rupiah(tagihan.menunggu_konfirmasi_idr)}</div></div>
            <div>
              <div className="text-xs text-muted-foreground">{tagihan.status_nominal === "LEBIH_BAYAR" ? (tagihan.lebih_dikembalikan_idr > 0 ? "Sisa lebih bayar" : "Lebih bayar") : "Sisa tagihan"}</div>
              <div className={`font-semibold tabular-nums ${tagihan.status_nominal === "LEBIH_BAYAR" ? "text-destructive" : tagihan.status_nominal === "SESUAI" ? "text-success" : ""}`} data-testid="fa-tagihan-sisa">
                {rupiah(tagihan.status_nominal === "LEBIH_BAYAR" ? tagihan.lebih_idr : tagihan.sisa_idr)}
              </div>
              {tagihan.lebih_dikembalikan_idr > 0 && (
                <div className="text-xs text-muted-foreground" data-testid="fa-tagihan-lebih-dikembalikan">
                  Kelebihan bayar {rupiah(tagihan.lebih_bayar_idr)}, sudah dikembalikan {rupiah(tagihan.lebih_dikembalikan_idr)}
                  {(tagihan.rincian_dikembalikan || []).length ? ` (${tagihan.rincian_dikembalikan.map((b) => b.refund_number).filter(Boolean).join(", ")})` : ""}
                </div>
              )}
              {tagihan.status_nominal === "LEBIH_BAYAR" && (
                <div className="text-xs text-destructive">
                  {tagihan.lebih_dikembalikan_idr > 0 ? "Sisa kelebihan wajib dikembalikan" : "Wajib dikembalikan"} — buat Refund sebab Lebih Bayar.
                </div>
              )}
            </div>
          </div>
        )}
        {/* Tanda Jadi */}
        <div className="border-l-4 border-border pl-4">
          <div className="flex items-center gap-2 mb-1"><Clock size={14} className="text-muted-foreground" /><span className="text-sm font-medium">Tanda Jadi (dari PD)</span></div>
          {payments.tanda_jadi ? (
            <div className="text-xs text-muted-foreground">
              {rupiah(payments.tanda_jadi.amount_idr)} · <PRStatusBadge value={payments.tanda_jadi.status} />
              {/* T88 / K227: tanda jadi kurang bayar — kekurangannya masuk tagihan pelunasan. */}
              {tagihan?.rencana?.tanda_jadi_kurang_idr > 0 && (
                <div className="text-warning-soft-foreground mt-0.5" data-testid="fa-tj-kurang">
                  Diterima {rupiah(tagihan.rencana.tanda_jadi_idr)} dari {rupiah(tagihan.rencana.tanda_jadi_pengajuan_idr)} —
                  kekurangan {rupiah(tagihan.rencana.tanda_jadi_kurang_idr)} ditambahkan ke tagihan pelunasan.
                </div>
              )}
            </div>
          ) : <div className="text-xs text-muted-foreground italic">Tidak ada data Tanda Jadi.</div>}
        </div>

        {/* DP */}
        <div className="border-l-4 border-info-soft-border pl-4">
          <div className="flex items-center gap-2 mb-2"><Clock size={14} className="text-info" /><span className="text-sm font-medium">DP {fa.dp_percentage}% — {rupiah(fa.dp_amount_idr)}</span></div>
          {fa.dp_confirmed_at ? (
            (tagihan?.rincian_dibayar || []).some((b) => b.jenis === "DP")
              ? <PembayaranTerkonfirmasi tagihan={tagihan} jenis="DP" label="DP" jam={jamFa} />
              : <div className="text-xs text-success-soft-foreground">DP dikonfirmasi pada {jamFa(fa.dp_confirmed_at)}.</div>
          ) : payments.dp?.status === "MENUNGGU_KONFIRMASI" ? (
            /* ⛔ 2 Sep 2026 — KONFIRMASI DP NOL PUNYA LAYAR sebelum ini. Catatan
               di bawah menunjuk /payments/pending; layar itu hanya memuat Tanda
               Jadi dan menunjuk balik ke sini. Keduanya saling menunjuk, dan
               23 FA menumpuk di "Menunggu DP" bukan karena customer nol bayar.
               Ditemukan saat MENELUSURI rantai, bukan oleh test. */
            <KonfirmasiPembayaranInline pr={payments.dp} label="DP" bolehPutus={isOwner || isKacab} bolehCek={isOwner || isKacab || isCS} isOwner={isOwner}
                                        busy={busy} callAction={callAction} testIdPrefix="fa-dp" jam={jamFa} />
          ) : canSubmitDP ? (
            <div className="space-y-2">
              {tagihan?.dp?.dp_dikonfirmasi_idr > 0 && (
                <div className="text-xs text-warning-soft-foreground" data-testid="fa-dp-kurang">
                  DP dikonfirmasi {rupiah(tagihan.dp.dp_dikonfirmasi_idr)} — kurang {rupiah(tagihan.dp.dp_kurang_idr)} dari DP wajib. Ajukan sisanya.
                </div>
              )}
              <IsianTransferManual storeId={fa.store_id} isian={dpProof} setIsian={setDpProof} testIdPrefix="fa-dp" />
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Input type="number" min="1" placeholder="Nominal ditransfer (Rp)" data-testid="fa-dp-nominal"
                       value={dpNominal !== "" ? dpNominal : String(tagihan?.dp?.dp_kurang_idr || fa.dp_amount_idr || "")}
                       onChange={(e) => setDpNominal(e.target.value)} />
              </div>
              <Button size="sm" disabled={!isianTransferLengkap(dpProof) || busy} onClick={() => callAction(() => api.post("/payments/submit", {
                payment_type: "DP", payment_method: "TRANSFER",
                amount_idr: Number(dpNominal !== "" ? dpNominal : (tagihan?.dp?.dp_kurang_idr || fa.dp_amount_idr)),
                proof_category: "TRANSFER_DP", fa_id: id,
                proof: muatanBukti(dpProof),
              }))} data-testid="fa-dp-submit-btn">Kirim DP</Button>
            </div>
          ) : <div className="text-xs text-muted-foreground italic" data-testid="fa-dp-keadaan">
                {/* T28: keterangan ikut keadaan, bukan satu kalimat tetap. */}
                {fa.order_status === "DRAFT_FA_CREATED" ? "FA masih draf — belum dikirim ke customer."
                  : fa.order_status === "FA_WAITING_CUSTOMER_APPROVAL" ? "Menunggu customer mengonfirmasi FA di Ruang Pesanan."
                  : fa.order_status === "FA_APPROVED_WAITING_DP" ? "Menunggu customer membayar uang muka (tautan DOKU di Ruang Pesanan, atau transfer)."
                  : "—"}
              </div>}
        </div>

        {/* Pelunasan */}
        <div className="border-l-4 border-success-soft-border pl-4">
          {/* T88 / K227: nominal pelunasan dari tanda jadi yang DITERIMA (`tagihan.rencana`), bukan kolom pengajuan FA. */}
          <div className="flex items-center gap-2 mb-2"><Clock size={14} className="text-success" /><span className="text-sm font-medium" data-testid="fa-pelunasan-judul">Pelunasan — {rupiah(tagihan?.rencana ? tagihan.rencana.pelunasan_idr : fa.pelunasan_amount_idr)}</span></div>
          {/* T106: pelunasan yang sudah dikonfirmasi tampil nominal AKTUAL + kapan + oleh siapa (dulu kolom
              `pelunasan_confirmed_at` yang tidak pernah ada → "Tidak ada tagihan pelunasan." sesudah LUNAS). */}
          <PembayaranTerkonfirmasi tagihan={tagihan} jenis="PELUNASAN" label="Pelunasan" jam={jamFa} />
          {payments.pelunasan?.status === "MENUNGGU_KONFIRMASI" ? (
            <KonfirmasiPembayaranInline pr={payments.pelunasan} label="Pelunasan" bolehPutus={isOwner || isKacab} bolehCek={isOwner || isKacab || isCS} isOwner={isOwner}
                                        busy={busy} callAction={callAction} testIdPrefix="fa-pelunasan" jam={jamFa} />
          ) : canSubmitPelunasan ? (
            <div className="space-y-2">
              <IsianTransferManual storeId={fa.store_id} isian={pelunasanProof} setIsian={setPelunasanProof} testIdPrefix="fa-pelunasan" />
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Input placeholder="Nominal ditransfer (Rp)" inputMode="numeric" data-testid="fa-pelunasan-nominal"
                       value={nominalPelunasan !== "" ? nominalPelunasan : String(tagihan ? tagihan.sisa_idr : (fa.pelunasan_amount_idr || ""))}
                       onChange={(e) => setNominalPelunasan(e.target.value.replace(/[^0-9]/g, ""))} />
              </div>
              <Button size="sm" disabled={!isianTransferLengkap(pelunasanProof) || busy} onClick={() => callAction(() => api.post("/payments/submit", {
                payment_type: "PELUNASAN", payment_method: "TRANSFER",
                amount_idr: Number(nominalPelunasan !== "" ? nominalPelunasan : (tagihan ? tagihan.sisa_idr : fa.pelunasan_amount_idr)),
                proof_category: "TRANSFER_PELUNASAN", fa_id: id,
                proof: muatanBukti(pelunasanProof),
              }))} data-testid="fa-pelunasan-submit-btn">Kirim Pelunasan</Button>
            </div>
          ) : <div className="text-xs text-muted-foreground italic" data-testid="fa-pelunasan-keadaan">
                {/* T28 (E1 Langkah 19): sesudah DP terkonfirmasi kalimat lama "Menunggu DP dikonfirmasi" usang. */}
                {STATUS_TANPA_TAGIHAN_PELUNASAN.includes(fa.order_status)
                  ? (tagihan && tagihan.total_tagihan_idr > 0 && tagihan.sisa_idr === 0 && fa.order_status !== "CANCELLED"
                    ? "Lunas — tidak ada sisa tagihan." : "Tidak ada tagihan pelunasan.")
                  : (fa.dp_confirmed_at || fa.is_foc) ? "Menunggu customer melunasi (tautan DOKU di Ruang Pesanan, atau transfer)."
                  : "Pelunasan ditagih sesudah uang muka diterima."}
              </div>}
        </div>

        <div className="text-xs text-muted-foreground pt-2 border-t" data-testid="fa-payment-note">
          {/* T28: sejak K208 pembayaran DOKU terkonfirmasi otomatis; transfer manual = cadangan (BUSINESS-98). */}
          Pembayaran lewat DOKU terkonfirmasi otomatis. Transfer manual (cadangan) dikonfirmasi Kepala Cabang <strong>di layar ini</strong> dengan
          mengetik nominal dari mutasi rekening. Semua bukti yang menunggu (tanda jadi, DP, pelunasan) juga terkumpul di <a href="/payments/pending" className="underline">Konfirmasi Pembayaran</a>.
        </div>
      </Card>

      {/* Deadline */}
      <Card className="p-6">
        <h2 className="font-semibold mb-2">Deadline Produksi</h2>
        {deadline === "PENDING_DP" || !deadline ? (
          <div className="text-sm text-muted-foreground">Menunggu DP dikonfirmasi sebelum deadline dihitung.</div>
        ) : (
          <div className="text-sm">
            <span className="text-muted-foreground">Deadline:</span> <span className="font-medium" data-testid="fa-deadline-date">{formatDate(deadline)}</span>
            <div className="text-xs text-muted-foreground mt-1">Dihitung otomatis dari tanggal DP dikonfirmasi + {fa.package_days} hari kerja (Senin–Sabtu, di luar hari libur).</div>
          </div>
        )}
      </Card>
    </div>
  );
}
